import { useTranslate } from '@im/react-localization';
import { ColumnDef } from 'src/components/TableComponent/types';
import { AuditLogRow } from '../hooks/useAuditLogsHooks';
import { StyledText } from 'src/components/StyledTypography';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

export type Rows = AuditLogRow & {
  id: number;
};

export const useAuditLogColumns = (
  selectedCategory?: string
): ColumnDef<Rows>[] => {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const [searchParams] = useSearchParams();
  const countryCode = String(searchParams.get('countryCode'));
  const companyCode = String(searchParams.get('companyCode'));
  const columns: ColumnDef<Rows>[] = [
    {
      field: 'id',
      headerName: '#',
      width: 50,
    },
    { field: 'vendor_Name', headerName: translate('AuditLogs.ColumnVendor') },
    {
      field: 'vendor_Program_Name',
      headerName: translate('AuditLogs.ColumnVendorProgram'),
      renderCell: (params) => {
        const category = params.row.category;

        if (category === 'Vendor-Program') {
          return (
            <StyledText
              data-testid="gds-vp-audit-logs-gds-vp-link"
              onClick={() => {
                window.open(
                  `/vendor-program/program-detail?vendorId=${params.row.vendor_ID}&programId=${params.row.vendor_Program_ID}&selectedTab=0&tab=changeLog&auditId=${params.row.auditId}&companyCode=${companyCode}&countryCode=${countryCode}`
                );
              }}
              variant="link"
            >
              {params.value}
            </StyledText>
          );
        }
        return <span>{params.value ?? '-'}</span>;
      },
    },
    {
      field: 'bid_Number',
      headerName: translate('AuditLogs.ColumnBidNumber'),
      renderCell: (params) => {
        const category = params.row.category;
        const isDeactivated = params.row.eventType === 'DealDeactivation';

        if (category === 'Bid') {
          if (isDeactivated) {
            return (
              <StyledText
                data-testid="gds-vp-audit-logs-bem-bid-number-link"
                style={{
                  color: '#A0A0A0',
                }}
                variant="body2"
              >
                {params.value}
              </StyledText>
            );
          }
          return (
            <StyledText
              data-testid="gds-vp-audit-logs-bem-bid-number-link"
              onClick={() => {
                window.open(
                  `/vendor-program/details-page?businessType=HWSW&dealNumber=${params.row.bid_Number}&dealid=${params.row.dealId}&customerset_id=0&tab=changeLog&auditId=${params.row.auditId}&countryCode=${countryCode}&companyCode=${companyCode}`
                );
              }}
              variant="link"
            >
              {params.value}
            </StyledText>
          );
        }
        return <span>{params.value ?? '-'}</span>;
      },
    },
    {
      field: 'beM_Number',
      headerName: translate('AuditLogs.ColumnBEMNumber'),
      renderCell: (params) => {
        const category = params.row.category;

        // If category is BEM, render hyperlink
        if (category === 'BEM') {
          return (
            <StyledText
              data-testid="gds-vp-audit-logs-bem-link"
              onClick={() => {
                window.open(
                  `/bid-management-hub/bid-data?refId=${params.row.beM_Number}&tab=changeLog&auditId=${params.row.auditId}&countryCode=${countryCode}&companyCode=${companyCode}`
                );
              }}
              variant="link"
            >
              {params.value}
            </StyledText>
          );
        }
        return <span>{params.value ?? '-'}</span>;
      },
    },
    { field: 'eventType', headerName: translate('AuditLogs.ColumnEventType') },
    {
      field: 'modifiedBy',
      headerName: translate('AuditLogs.ColumnModifiedBy'),
    },
    {
      field: 'modifiedDate',
      headerName: translate('AuditLogs.ColumnModifiedDate'),
      renderCell: (params) =>
        typeof params.value === 'string'
          ? formatDateTime(params.value, true)
          : `--`,
    },
  ];

  return columns.filter((column) => {
    if (selectedCategory === 'Vendor-Program') {
      return column.field !== 'bid_Number' && column.field !== 'beM_Number';
    }

    if (selectedCategory === 'Bid') {
      return column.field !== 'beM_Number';
    }

    return true;
  });
};
